const HISTORY_KEY = 'sukoji:daily:history:v1'
const HISTORY_LIMIT = 400
const PARIS_TIME_ZONE = 'Europe/Paris'

function finiteNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    const number = Number(value)
    if (Number.isFinite(number)) return number
  }
  return null
}

export function parisDayKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PARIS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const read = (type) => parts.find((part) => part.type === type)?.value
  return `${read('year')}-${read('month')}-${read('day')}`
}

export function shiftDay(day, delta) {
  const date = new Date(`${day}T12:00:00.000Z`)
  if (Number.isNaN(date.getTime())) return day
  date.setUTCDate(date.getUTCDate() + delta)
  return date.toISOString().slice(0, 10)
}

export function readLocalHistory() {
  try {
    const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry?.day || ''))
      .slice(0, HISTORY_LIMIT)
  } catch {
    return []
  }
}

export function recordDailyCompletion(day, result = {}, extra = {}) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return readLocalHistory()
  const existing = readLocalHistory()
  const previous = existing.find((entry) => entry.day === day) || {}
  const elapsedSeconds = finiteNumber(
    result.elapsedSeconds,
    result.elapsed_seconds,
    Number(result.elapsedMs ?? result.elapsed_ms) / 1000,
    previous.elapsedSeconds,
  )
  const entry = {
    ...previous,
    day,
    ...(elapsedSeconds !== null && elapsedSeconds >= 0 ? { elapsedSeconds } : {}),
    completedAt: finiteNumber(result.completedAt, result.completed_at, previous.completedAt, Date.now()),
    assisted: Boolean(result.assisted ?? previous.assisted),
    rank: finiteNumber(result.rank, extra.rank, previous.rank),
    participants: finiteNumber(extra.participants, previous.participants),
    medianSeconds: finiteNumber(extra.medianSeconds, previous.medianSeconds),
  }
  const next = [entry, ...existing.filter((item) => item.day !== day)]
    .sort((left, right) => right.day.localeCompare(left.day))
    .slice(0, HISTORY_LIMIT)
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)) } catch { /* stockage indisponible */ }
  window.dispatchEvent(new CustomEvent('sukoji:progress-updated', { detail: entry }))
  return next
}

export function normalizeRemoteHistory(payload) {
  const source = payload?.history || payload?.entries || payload?.results || []
  if (!Array.isArray(source)) return []
  return source.flatMap((entry) => {
    const day = entry?.day || entry?.date
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return []
    const elapsedMs = finiteNumber(entry.elapsedMs, entry.elapsed_ms)
    const elapsedSeconds = finiteNumber(
      entry.elapsedSeconds,
      entry.elapsed_seconds,
      elapsedMs === null ? null : elapsedMs / 1000,
    )
    return [{
      day,
      ...(elapsedSeconds !== null && elapsedSeconds >= 0 ? { elapsedSeconds } : {}),
      completedAt: finiteNumber(entry.completedAt, entry.completed_at),
      assisted: Boolean(entry.assisted),
      rank: finiteNumber(entry.rank),
      participants: finiteNumber(entry.participants, entry.playerCount, entry.player_count),
      medianSeconds: finiteNumber(entry.medianSeconds, entry.median_seconds),
    }]
  })
}

export function mergeHistory(remote, local) {
  const byDay = new Map()
  for (const entry of [...local, ...remote]) {
    const previous = byDay.get(entry.day) || {}
    byDay.set(entry.day, { ...previous, ...entry })
  }
  return [...byDay.values()].sort((left, right) => right.day.localeCompare(left.day))
}

export function calculateProgress(history, remoteStats = null) {
  const completedDays = new Set(history.map((entry) => entry.day))
  const today = parisDayKey()
  let cursor = completedDays.has(today) ? today : shiftDay(today, -1)
  let localCurrentStreak = 0
  while (completedDays.has(cursor)) {
    localCurrentStreak += 1
    cursor = shiftDay(cursor, -1)
  }

  let localBestStreak = 0
  let run = 0
  let previous = null
  for (const day of [...completedDays].sort()) {
    run = previous && shiftDay(previous, 1) === day ? run + 1 : 1
    localBestStreak = Math.max(localBestStreak, run)
    previous = day
  }
  const times = history.map((entry) => entry.elapsedSeconds).filter(Number.isFinite)
  const readStat = (...keys) => {
    for (const key of keys) {
      const value = finiteNumber(remoteStats?.[key])
      if (value !== null) return value
    }
    return null
  }
  const bestElapsedMs = readStat('bestElapsedMs', 'best_elapsed_ms')
  const averageElapsedMs = readStat('averageElapsedMs', 'average_elapsed_ms')
  const medianElapsedMs = readStat('medianElapsedMs', 'median_elapsed_ms')
  return {
    currentStreak: readStat('currentStreak', 'current_streak') ?? localCurrentStreak,
    bestStreak: readStat('bestStreak', 'best_streak', 'longestStreak', 'longest_streak') ?? localBestStreak,
    total: readStat('completedCount', 'completed_count', 'total', 'totalCompletions', 'total_completions') ?? history.length,
    bestSeconds: readStat('bestSeconds', 'best_seconds')
      ?? (bestElapsedMs === null ? null : bestElapsedMs / 1000)
      ?? (times.length ? Math.min(...times) : null),
    averageSeconds: readStat('averageSeconds', 'average_seconds')
      ?? (averageElapsedMs !== null ? averageElapsedMs / 1000 : medianElapsedMs !== null ? medianElapsedMs / 1000 : null)
      ?? (times.length ? times.reduce((sum, value) => sum + value, 0) / times.length : null),
    averageLabel: averageElapsedMs === null && medianElapsedMs !== null ? 'méd.' : 'moy.',
  }
}

function offsetAt(timeZone, instant) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const get = (type) => Number(parts.find((part) => part.type === type)?.value)
  const representedAsUtc = Date.UTC(
    get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'),
  )
  return representedAsUtc - instant.getTime()
}

export function millisecondsUntilNextDaily(now = new Date()) {
  const tomorrow = shiftDay(parisDayKey(now), 1)
  const [year, month, day] = tomorrow.split('-').map(Number)
  const wallClockAsUtc = Date.UTC(year, month - 1, day)
  let candidate = wallClockAsUtc - offsetAt(PARIS_TIME_ZONE, new Date(wallClockAsUtc))
  candidate = wallClockAsUtc - offsetAt(PARIS_TIME_ZONE, new Date(candidate))
  return Math.max(0, candidate - now.getTime())
}
